/**
 * one-fl-nw-new-bundle.js
 *
 * Skip-modal analog of the NW New Bundle CTA checkout path in nw-new-bundle.js.
 * Adds one configured flavor variant using the checked bundle tier's quantity,
 * selling plan, discount code, clear-cart, drawer, and checkout-upsell flow.
 * Does not bind flavors-modal openers or duplicate radio/gift listeners.
 */
(function () {
  'use strict';

  if (window.__oneFlNwNewBundleInstalled) return;
  window.__oneFlNwNewBundleInstalled = true;

  const _fetch = window.__originalFetch || window.fetch.bind(window);

  const SELECTOR = {
    button: '[data-one-fl-nw-new-bundle-cta]',
    bundleRadio: '.nw-new-bundle-option input[name="nw-new-bundle-option"]',
    flavorsModal: '#nw-new-bundle-flavors-modal',
    flavorsDialog: '.nw-new-bundle-flavors-modal__dialog',
    checkoutUpsellRoot: '#nw-checkout-upsell-root',
    checkoutUpsellModal: '#nw-new-bundle-checkout-upsell-modal',
  };

  const PURCHASE_SUBSCRIPTION = 'subscription';
  const PURCHASE_ONETIME = 'onetime';

  let checkoutInFlight = false;

  function parsePositiveInt(raw) {
    const n = parseInt(String(raw ?? '0'), 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }

  function getActiveBundleRadio() {
    const checked = document.querySelector(`${SELECTOR.bundleRadio}:checked`);
    if (checked instanceof HTMLInputElement) return checked;
    const first = document.querySelector(SELECTOR.bundleRadio);
    return first instanceof HTMLInputElement ? first : null;
  }

  function getFlavorsDialog() {
    const modal = document.querySelector(SELECTOR.flavorsModal);
    const dialog = modal?.querySelector(SELECTOR.flavorsDialog);
    return dialog instanceof HTMLElement ? dialog : null;
  }

  function cartAjaxJsUrl(routeUrl, fallback) {
    const base = typeof routeUrl === 'string' && routeUrl.length ? routeUrl : fallback;
    if (base.endsWith('.js')) return base;
    return `${base.replace(/\/?$/, '')}.js`;
  }

  function shopifyRootPath() {
    const raw = window.Shopify?.routes?.root;
    if (typeof raw !== 'string' || !raw.length) return '/';
    return raw.endsWith('/') ? raw : `${raw}/`;
  }

  function getStoreCheckoutUrl() {
    const raw = window.routes?.cart_url;
    if (typeof raw !== 'string' || !raw.length) return '/checkout';
    try {
      const u = new URL(raw, window.location.origin);
      u.pathname = u.pathname.replace(/\/cart\/?$/, '/checkout');
      return `${u.pathname}${u.search}`;
    } catch {
      return '/checkout';
    }
  }

  function getCheckoutUrlWithDiscountCodes(codes) {
    const checkout = getStoreCheckoutUrl();
    const list = (Array.isArray(codes) ? codes : [])
      .map((c) => String(c ?? '').trim())
      .filter(Boolean);
    if (!list.length) return checkout;
    const encoded = list.map((c) => encodeURIComponent(c)).join(',');
    return `${shopifyRootPath()}discount/${encoded}?redirect=${encodeURIComponent(checkout)}`;
  }

  function normalizePurchaseType(raw) {
    const s = String(raw ?? '').trim().toLowerCase();
    if (s === PURCHASE_ONETIME || s === 'one-time' || s === 'one_time') return PURCHASE_ONETIME;
    if (s === PURCHASE_SUBSCRIPTION || s === 'subscribe' || s === 'sub') return PURCHASE_SUBSCRIPTION;
    return null;
  }

  function getButtonPurchaseType(btn) {
    return normalizePurchaseType(btn.getAttribute('data-purchase-type')) || PURCHASE_SUBSCRIPTION;
  }

  function parsePlanPricing(root) {
    const script = root.querySelector('script[type="application/json"][data-one-fl-plan-pricing]');
    if (!script || !(script instanceof HTMLScriptElement) || !String(script.textContent || '').trim()) {
      return {};
    }
    try {
      const j = JSON.parse(String(script.textContent));
      if (!j || typeof j !== 'object' || Array.isArray(j)) return {};
      return j;
    } catch {
      return {};
    }
  }

  function resolveSellingPlanId(btn, radio) {
    const defaultPlan = parsePositiveInt(btn.getAttribute('data-flavor-default-selling-plan-id'));
    const rawAttr = radio?.getAttribute('data-flavors-selling-plan-id');
    const trimmed = rawAttr != null ? String(rawAttr).trim() : '';
    const customPlanNumeric = trimmed ? parsePositiveInt(trimmed) : 0;
    if (!trimmed || customPlanNumeric <= 0) return defaultPlan;

    const pricing = parsePlanPricing(btn.closest('.one-fl-nw-new-bundle-cta') || btn.parentElement || btn);
    const keys = [trimmed];
    if (String(customPlanNumeric) !== trimmed) keys.push(String(customPlanNumeric));
    const hasPricing = Object.keys(pricing).length > 0;
    if (!hasPricing) return customPlanNumeric;
    for (let i = 0; i < keys.length; i += 1) {
      if (pricing[keys[i]] != null) return customPlanNumeric;
    }
    return defaultPlan;
  }

  function getFlavorsDiscountCode() {
    const dialog = getFlavorsDialog();
    return String(dialog?.getAttribute('data-flavors-discount-code') ?? '').trim();
  }

  function getFlavorsDiscountCodeForPurchase(purchaseType) {
    if (purchaseType !== PURCHASE_SUBSCRIPTION) return '';
    return getFlavorsDiscountCode();
  }

  function shouldOpenCartDrawerAfterCheckout() {
    const dialog = getFlavorsDialog();
    return dialog?.getAttribute('data-open-cart-drawer') === 'true';
  }

  function flavorsCtaWillRedirectToCheckout() {
    if (!shouldOpenCartDrawerAfterCheckout()) return true;
    const onPage = window.location.pathname.indexOf('/pages/') !== -1;
    return Boolean(
      window.__cartDrawerDisabled ||
      (window.__cartDrawerForceCheckoutOnPages && onPage) ||
      window.__cartDrawerForceCheckoutOnProduct
    );
  }

  function shouldClearCartBeforeCheckout(btn) {
    const scope = btn.closest('product-info, .product__info-container, [data-section]');
    const config = scope?.querySelector('.nw-new-bundle-flavors-pdp-config');
    if (config) {
      const raw = config.getAttribute('data-nw-clear-cart-before-checkout');
      if (raw != null) return String(raw).trim().toLowerCase() !== 'false';
    }
    const dialog = getFlavorsDialog();
    const raw = dialog?.getAttribute('data-nw-clear-cart-before-checkout');
    if (raw == null) return true;
    return String(raw).trim().toLowerCase() !== 'false';
  }

  function getCheckoutUpsellRoot() {
    const el = document.querySelector(SELECTOR.checkoutUpsellRoot);
    return el instanceof HTMLElement ? el : null;
  }

  function getCheckoutUpsellModal() {
    const el = document.querySelector(SELECTOR.checkoutUpsellModal);
    return el instanceof HTMLElement ? el : null;
  }

  function isCheckoutUpsellEnabled() {
    const root = getCheckoutUpsellRoot();
    if (!root) return false;
    const raw = root.getAttribute('data-nw-checkout-upsell-enabled');
    return String(raw ?? '').trim().toLowerCase() !== 'false';
  }

  function getCheckoutUpsellPanel() {
    const root = getCheckoutUpsellRoot();
    if (!root) return null;
    const panel = root.querySelector('[data-upsell-panel]');
    return panel instanceof HTMLElement ? panel : null;
  }

  function isCheckoutUpsellPanelAvailable(panel) {
    const raw = String(panel.getAttribute('data-upsell-available') ?? '').trim().toLowerCase();
    return raw !== 'false' && raw !== '0';
  }

  function getCheckoutUpsellSkipThreshold() {
    const root = getCheckoutUpsellRoot();
    const raw =
      root?.getAttribute('data-upsell-skip-at-boxes') ??
      root?.getAttribute('data-upsell-c-skip-at-boxes');
    if (raw == null || String(raw).trim() === '') return 6;
    const n = parsePositiveInt(raw);
    if (n === 0) return 0;
    return n;
  }

  function shouldShowCheckoutUpsell(totalBoxes) {
    if (!isCheckoutUpsellEnabled()) return false;
    const panel = getCheckoutUpsellPanel();
    if (!panel || !isCheckoutUpsellPanelAvailable(panel)) return false;
    const threshold = getCheckoutUpsellSkipThreshold();
    if (threshold > 0 && totalBoxes >= threshold) return false;
    return true;
  }

  function discountCodesFromCart(cart) {
    const out = [];
    const seen = new Set();
    const push = (raw) => {
      const code = String(raw ?? '').trim();
      if (!code) return;
      const key = code.toUpperCase();
      if (seen.has(key)) return;
      seen.add(key);
      out.push(code);
    };
    if (cart && Array.isArray(cart.discount_codes)) {
      cart.discount_codes.forEach((entry) => {
        push(typeof entry === 'string' ? entry : entry && entry.code);
      });
    }
    if (cart && typeof cart.discount_code === 'string') push(cart.discount_code);
    return out;
  }

  async function mergeCartDiscountCodes(nextCode) {
    const code = String(nextCode ?? '').trim();
    const cartUrl = cartAjaxJsUrl(window.routes?.cart_url, '/cart');
    let existing = [];
    try {
      const res = await _fetch(cartUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (res.ok) existing = discountCodesFromCart(await res.json());
    } catch {
      /* keep existing empty */
    }
    if (!code) return existing;
    const upper = code.toUpperCase();
    if (existing.some((c) => c.toUpperCase() === upper)) return existing;
    return existing.concat(code);
  }

  function cartHasApplicableCode(cart, code) {
    const upper = String(code ?? '').trim().toUpperCase();
    if (!upper) return true;
    const entries = Array.isArray(cart && cart.discount_codes) ? cart.discount_codes : [];
    return entries.some((entry) => {
      const name = String(
        typeof entry === 'string' ? entry : entry && entry.code ? entry.code : ''
      ).toUpperCase();
      if (name !== upper) return false;
      if (entry && typeof entry === 'object' && 'applicable' in entry) {
        return Boolean(entry.applicable);
      }
      return true;
    });
  }

  async function pingDiscountEndpoint(codes) {
    const list = (Array.isArray(codes) ? codes : [])
      .map((c) => String(c ?? '').trim())
      .filter(Boolean);
    if (!list.length) return false;
    const url = `${shopifyRootPath()}discount/${list.map((c) => encodeURIComponent(c)).join(',')}`;
    try {
      await _fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        redirect: 'manual',
      });
      return true;
    } catch (e) {
      console.warn('[OneFlNwNewBundle] GET /discount fallback failed', e);
      return false;
    }
  }

  async function applyCartDiscountCode(discountCode) {
    const code = String(discountCode ?? '').trim();
    if (!code) return true;

    const merged = await mergeCartDiscountCodes(code);
    const updateUrl = cartAjaxJsUrl(window.routes?.cart_update_url, '/cart/update');
    try {
      const res = await _fetch(updateUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: merged.join(',') }),
      });
      if (!res.ok) {
        console.warn('[OneFlNwNewBundle] cart/update discount failed', { code, merged, status: res.status });
        return pingDiscountEndpoint(merged);
      }
      let cart = null;
      try {
        cart = await res.json();
      } catch {
        cart = null;
      }
      if (cartHasApplicableCode(cart, code)) return true;
      return pingDiscountEndpoint(merged);
    } catch (e) {
      console.warn('[OneFlNwNewBundle] cart/update discount error', e);
      return pingDiscountEndpoint(merged);
    }
  }

  async function syncFreeGiftsBeforeCheckout() {
    try {
      if (window.cartFreeGifts && typeof window.cartFreeGifts.sync === 'function') {
        await window.cartFreeGifts.sync();
        return;
      }
      if (window.cartDrawerCustom && typeof window.cartDrawerCustom.refresh === 'function') {
        await window.cartDrawerCustom.refresh();
      }
    } catch (e) {
      console.warn('[OneFlNwNewBundle] free-gift sync before checkout failed', e);
    }
  }

  async function redirectToStoreCheckout(discountCode) {
    if (window.__larineIsGumIgHideChrome && window.__larineIsGumIgHideChrome()) {
      try {
        sessionStorage.setItem('larineBundleCheckoutRedirect', '1');
        localStorage.setItem('larineBundleCheckoutRedirect', '1');
      } catch {
        /* private mode */
      }
      try {
        var cookie = 'larineBundleCheckoutRedirect=1; path=/; max-age=3600; SameSite=Lax';
        if (location.protocol === 'https:') cookie += '; Secure';
        document.cookie = cookie;
      } catch {
        /* private mode */
      }
    }
    await syncFreeGiftsBeforeCheckout();
    const code = String(discountCode ?? '').trim();
    if (!code) {
      window.location.href = getStoreCheckoutUrl();
      return;
    }
    const merged = await mergeCartDiscountCodes(code);
    window.location.href = getCheckoutUrlWithDiscountCodes(merged);
  }

  function openCheckoutUpsellModal() {
    const modal = getCheckoutUpsellModal();
    const panel = getCheckoutUpsellPanel();
    if (!modal || !panel) return false;
    panel.classList.remove('hidden');
    panel.hidden = false;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('nw-new-bundle-checkout-upsell-open');
    return true;
  }

  async function finishCheckoutNavigation(purchaseType) {
    const stayInDrawer = !flavorsCtaWillRedirectToCheckout();
    if (stayInDrawer) {
      if (window.cartDrawerCustom) {
        const openAfterRefresh = () => {
          if (typeof window.cartDrawerCustom.open === 'function') {
            window.cartDrawerCustom.open(false);
          }
        };
        if (typeof window.cartDrawerCustom.refresh === 'function') {
          await Promise.resolve(window.cartDrawerCustom.refresh());
        }
        openAfterRefresh();
      }
      return;
    }
    await redirectToStoreCheckout(getFlavorsDiscountCodeForPurchase(purchaseType) || undefined);
  }

  async function finishCheckoutFlow(totalBoxes, purchaseType) {
    resetButtonLoadingUi();
    if (shouldShowCheckoutUpsell(totalBoxes) && openCheckoutUpsellModal()) return;
    await finishCheckoutNavigation(purchaseType);
  }

  function postCartAddItems(addUrl, items) {
    return _fetch(addUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
  }

  async function clearCart(clearUrl) {
    const clearRes = await _fetch(clearUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!clearRes.ok) throw new Error('Could not clear cart');
  }

  async function readCartAddErrorMessage(res) {
    let msg = 'Could not add items to cart';
    try {
      const err = await res.json();
      if (err && typeof err.description === 'string') msg = err.description;
      else if (err && typeof err.message === 'string') msg = err.message;
      else if (err && typeof err.status === 'string') msg = err.status;
    } catch {
      /* keep default */
    }
    return msg;
  }

  function setButtonLoading(btn, isLoading) {
    btn.classList.toggle('loading', isLoading);
    btn.disabled = isLoading;
    if (isLoading) {
      btn.setAttribute('aria-busy', 'true');
      btn.setAttribute('aria-disabled', 'true');
    } else {
      btn.removeAttribute('aria-busy');
      if (parsePositiveInt(btn.getAttribute('data-flavor-id')) > 0) {
        btn.removeAttribute('aria-disabled');
      }
    }
    const spinner = btn.querySelector('.loading-overlay__spinner');
    if (spinner instanceof HTMLElement) spinner.classList.toggle('hidden', !isLoading);
  }

  function resetButtonLoadingUi() {
    document.querySelectorAll(SELECTOR.button).forEach((el) => {
      if (el instanceof HTMLButtonElement) setButtonLoading(el, false);
    });
  }

  async function handleOneFlAdd(btn) {
    if (checkoutInFlight) return;
    if (!(btn instanceof HTMLButtonElement)) return;

    const flavorId = parsePositiveInt(btn.getAttribute('data-flavor-id'));
    if (flavorId <= 0) return;

    const radio = getActiveBundleRadio();
    const qty = Math.max(1, parsePositiveInt(radio?.getAttribute('data-item-count')));
    const purchaseType = getButtonPurchaseType(btn);
    const isSub = purchaseType === PURCHASE_SUBSCRIPTION;
    const planId = isSub ? resolveSellingPlanId(btn, radio) : 0;

    /** @type {{ id: number, quantity: number, selling_plan?: number }} */
    const item = { id: flavorId, quantity: qty };
    if (isSub && planId > 0) item.selling_plan = planId;

    checkoutInFlight = true;
    setButtonLoading(btn, true);
    window.__cartDrawerUpdating = true;

    try {
      const addUrl = cartAjaxJsUrl(window.routes?.cart_add_url, '/cart/add');
      const shouldClear = shouldClearCartBeforeCheckout(btn);
      const clearUrl = cartAjaxJsUrl(window.routes?.cart_clear_url, '/cart/clear');

      if (shouldClear) await clearCart(clearUrl);

      let addRes = await postCartAddItems(addUrl, [item]);
      if (!addRes.ok && shouldClear) {
        await clearCart(clearUrl);
        addRes = await postCartAddItems(addUrl, [item]);
      }
      if (!addRes.ok) {
        const msg = await readCartAddErrorMessage(addRes);
        console.error('[OneFlNwNewBundle] cart/add failed', {
          status: addRes.status,
          message: msg,
          item,
          purchaseType,
        });
        throw new Error(msg);
      }

      const discountCode = getFlavorsDiscountCodeForPurchase(purchaseType);
      const goingStraightToCheckout =
        flavorsCtaWillRedirectToCheckout() && !shouldShowCheckoutUpsell(qty);
      if (discountCode && !goingStraightToCheckout) {
        await applyCartDiscountCode(discountCode);
      }

      await finishCheckoutFlow(qty, purchaseType);
    } catch (e) {
      console.error('[OneFlNwNewBundle] checkout failed', e);
      resetButtonLoadingUi();
    } finally {
      checkoutInFlight = false;
      window.__cartDrawerUpdating = false;
    }
  }

  function onDocumentClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const btn = target.closest(SELECTOR.button);
    if (!btn) return;
    event.preventDefault();
    void handleOneFlAdd(btn);
  }

  function onPageLifecycle(event) {
    if (!event.persisted) return;
    resetButtonLoadingUi();
  }

  document.addEventListener('click', onDocumentClick);
  window.addEventListener('pagehide', onPageLifecycle);
  window.addEventListener('pageshow', onPageLifecycle);
})();
